import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { validateZipcode } from '../../redux/slices/ZipCode/zipCodeSlice';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export default function ZipcodeModal({ isOpen, onClose }) {
  const { t } = useTranslation('common');
  const [zipcode, setZipcode] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading } = useSelector((state) => state.zipcode);

  const handleZipChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    if (/^\d*$/.test(value)) {
      setZipcode(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (zipcode.length !== 5) return;
    try {
      const result = await dispatch(validateZipcode(zipcode)).unwrap();
      if (result.valid) {
        toast.success(t('zipcode.success'));
        onClose();
        setTimeout(() => navigate('/profile'), 500);
      } else {
        toast.error(t('zipcode.error'));
      }
    } catch {
      toast.error(t('zipcode.error'));
    }
  };

  const isValidLength = zipcode.length === 5;
  const showErrorBorder = zipcode.length > 0 && !isValidLength;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Toaster position="top-center" />
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  {t('zipcode.title')}
                </Dialog.Title>
                <form onSubmit={handleSubmit} className="mt-4">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{5}"
                    maxLength="5"
                    placeholder={t('zipcode.placeholder')}
                    value={zipcode}
                    onChange={handleZipChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-red-500 focus:border-red-500 ${
                      showErrorBorder ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                  />
                  {/* No error text message */}
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading || !isValidLength}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-red-300"
                    >
                      {isLoading ? t('zipcode.checking') : t('zipcode.continue')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}